import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  Globe,
  Moon,
  Sun,
  Share2,
  Download,
  Sparkles,
  MoreHorizontal,
  ChevronRight,
  FileText,
  FileCode,
  FileDown,
  Languages,
  Wand2,
  AlignLeft,
  Copy,
  Loader2,
  Check,
} from 'lucide-react';
import { useUIStore, useAuthStore, useDocumentStore } from '../lib/store';
import { cn } from '../lib/utils';
import { ConnectionStatus, CollaborationUser } from '../lib/collaboration';
import { UserAvatars, ConnectionStatusIndicator } from './CollaborationStatus';
import { aiApi } from '../lib/api';

interface TopBarProps {
  documentId?: string;
  status?: ConnectionStatus;
  users?: CollaborationUser[];
  lastSaved?: number | null;
}

export default function TopBar({ documentId, status, users = [], lastSaved }: TopBarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, toggleSidebar, theme, setTheme } = useUIStore();
  const { user } = useAuthStore();
  const { documents } = useDocumentStore();

  const [showAiMenu, setShowAiMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const aiMenuRef = useRef<HTMLDivElement>(null);

  const currentDocument = documentId ? documents.find(d => d.id === documentId) : null;
  const isDocumentPage = location.pathname.startsWith('/document/');
  const lang: 'en' | 'ar' = i18n.language.startsWith('ar') ? 'ar' : 'en';

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleAiAction = async (action: 'summarize' | 'translate' | 'continue' | 'improve') => {
    if (!currentDocument?.content) {
      setAiError(lang === 'ar' ? 'أضف محتوى لاستخدام الذكاء الاصطناعي' : 'Add some content to use AI tools.');
      return;
    }

    setAiLoading(action);
    setAiError(null);
    setCopied(false);
    
    try {
      let output = '';

      switch (action) {
        case 'summarize':
          ({ summary: output } = await aiApi.summarize(currentDocument.content, 'paragraph', lang));
          break;
        case 'translate':
          const from = lang === 'ar' ? 'ar' : 'en';
          const to = lang === 'ar' ? 'en' : 'ar';
          ({ translation: output } = await aiApi.translate(currentDocument.content, from, to));
          break;
        case 'continue':
          ({ result: output } = await aiApi.write(currentDocument.content, 'continue', lang));
          break;
        case 'improve':
          ({ result: output } = await aiApi.write(currentDocument.content, 'improve', lang));
          break;
      }

      setAiResult(output.trim());
    } catch (error) {
      console.error('AI action failed:', error);
      setAiError(lang === 'ar' ? 'تعذر تشغيل الذكاء الاصطناعي الآن.' : 'AI request failed. Please try again.');
    } finally {
      setAiLoading(null);
    }
  };

  const handleExport = (format: 'pdf' | 'markdown' | 'html') => {
    // TODO: Implement export functionality
    console.log(`Export as ${format}`);
    setShowExportMenu(false);
  };

  const handleShare = () => {
    if (documentId) {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  // Close AI menu on outside click or Escape
  useEffect(() => {
    if (!showAiMenu) return;

    const handleClick = (event: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(event.target as Node)) {
        setShowAiMenu(false);
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowAiMenu(false);
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showAiMenu]);

  const handleCopyResult = () => {
    if (!aiResult) return;
    navigator.clipboard.writeText(aiResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center justify-between gap-2 border-b bg-background/80 backdrop-blur-sm px-3">
      {/* Left side */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Menu toggle */}
        {!sidebarOpen && (
          <button
            onClick={toggleSidebar}
            className="btn-icon"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-4 w-4" />
          </button>
        )}

        {/* Breadcrumb */}
        {isDocumentPage && currentDocument && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0">
            <button
              onClick={() => navigate('/')}
              className="hover:text-foreground transition-colors shrink-0"
            >
              {t('nav.home')}
            </button>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 flip-rtl" />
            <span className="truncate font-medium text-foreground">
              {currentDocument.icon && <span className="me-1">{currentDocument.icon}</span>}
              {currentDocument.title || t('document.untitled')}
            </span>
          </div>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-1">
        {/* Collaboration status */}
        {isDocumentPage && status && (
          <div className="hidden sm:flex items-center gap-2 me-2">
            <UserAvatars users={users} maxDisplay={3} />
            <ConnectionStatusIndicator status={status} lastSaved={lastSaved} />
          </div>
        )}

        {/* AI Menu */}
        {isDocumentPage && (
          <div className="relative" ref={aiMenuRef}>
            <button
              onClick={() => setShowAiMenu(!showAiMenu)}
              className={cn('btn-ghost gap-1', showAiMenu && 'bg-secondary')}
            >
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">{t('AI') || 'AI'}</span>
            </button>

            {showAiMenu && (
              <div className="dropdown-content absolute end-0 top-full mt-2 w-80 shadow-2xl border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span>{lang === 'ar' ? 'مساعد الذكاء' : 'AI Assistant'}</span>
                  </div>
                  {aiLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                </div>

                <div className="p-2 space-y-1">
                  <button
                    onClick={() => handleAiAction('summarize')}
                    disabled={!!aiLoading}
                    className={cn('menu-item', aiLoading === 'summarize' && 'bg-secondary')}
                  >
                    <AlignLeft className="h-4 w-4" />
                    <span>{lang === 'ar' ? 'تلخيص فقرة' : 'Summarize document'}</span>
                  </button>
                  <button
                    onClick={() => handleAiAction('translate')}
                    disabled={!!aiLoading}
                    className={cn('menu-item', aiLoading === 'translate' && 'bg-secondary')}
                  >
                    <Languages className="h-4 w-4" />
                    <span>{lang === 'ar' ? 'ترجمة للعربية/الإنجليزية' : 'Translate to Arabic/English'}</span>
                  </button>
                  <button
                    onClick={() => handleAiAction('continue')}
                    disabled={!!aiLoading}
                    className={cn('menu-item', aiLoading === 'continue' && 'bg-secondary')}
                  >
                    <Wand2 className="h-4 w-4" />
                    <span>{lang === 'ar' ? 'إكمال الكتابة' : 'Continue writing'}</span>
                  </button>
                  <button
                    onClick={() => handleAiAction('improve')}
                    disabled={!!aiLoading}
                    className={cn('menu-item', aiLoading === 'improve' && 'bg-secondary')}
                  >
                    <Sparkles className="h-4 w-4" />
                    <span>{lang === 'ar' ? 'تحسين الأسلوب' : 'Improve tone'}</span>
                  </button>
                </div>

                {(aiResult || aiError) && (
                  <div className="border-t px-3 py-2 space-y-2">
                    {aiError && (
                      <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-2 py-1.5">
                        {aiError}
                      </div>
                    )}
                    {aiResult && (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{lang === 'ar' ? 'الناتج' : 'AI output'}</span>
                          <button onClick={handleCopyResult} className="inline-flex items-center gap-1 text-[11px] hover:text-foreground">
                            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            <span>{copied ? (lang === 'ar' ? 'تم النسخ' : 'Copied') : (lang === 'ar' ? 'نسخ' : 'Copy')}</span>
                          </button>
                        </div>
                        <div className="max-h-32 overflow-y-auto rounded border bg-muted/40 px-2.5 py-2 text-sm whitespace-pre-wrap" dir="auto">
                          {aiResult}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Share button */}
        {isDocumentPage && (
          <button onClick={handleShare} className="btn-ghost gap-1">
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('document.share')}</span>
          </button>
        )}

        {/* Export Menu */}
        {isDocumentPage && (
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className={cn('btn-icon', showExportMenu && 'bg-secondary')}
              title={t('document.export')}
            >
              <Download className="h-4 w-4" />
            </button>

            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                <div className="dropdown-content absolute end-0 top-full mt-1 w-40">
                  <button onClick={() => handleExport('pdf')} className="menu-item">
                    <FileDown className="h-4 w-4" />
                    <span>PDF</span>
                  </button>
                  <button onClick={() => handleExport('markdown')} className="menu-item">
                    <FileText className="h-4 w-4" />
                    <span>Markdown</span>
                  </button>
                  <button onClick={() => handleExport('html')} className="menu-item">
                    <FileCode className="h-4 w-4" />
                    <span>HTML</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="h-5 w-px bg-border mx-1" />

        {/* Language toggle */}
        <button
          onClick={toggleLanguage}
          className="btn-ghost gap-1"
          aria-label={t('settings.language')}
        >
          <Globe className="h-4 w-4" />
          <span className="text-xs font-medium">
            {i18n.language === 'en' ? 'ع' : 'EN'}
          </span>
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="btn-icon"
          aria-label={t('settings.theme')}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </button>

        {/* More menu */}
        <div className="relative">
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={cn('btn-icon', showMoreMenu && 'bg-secondary')}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>

          {showMoreMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
              <div className="dropdown-content absolute end-0 top-full mt-1 w-48">
                <button onClick={() => navigate('/settings')} className="menu-item">
                  <span>{t('nav.settings')}</span>
                </button>
                {user && (
                  <button className="menu-item text-destructive">
                    <span>{t('settings.logout')}</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
